// Analytics service that respects user consent
// This is a wrapper for Google Analytics or any other analytics provider

interface AnalyticsEvent {
    category: string;
    action: string;
    label?: string;
    value?: number;
}

interface AnalyticsPageView {
    path: string;
    title?: string;
}

// Check if we're in production - only send analytics in production
const isProduction = process.env.NODE_ENV === 'production';

// Flag to track if Google Analytics has been initialized
let gaInitialized = false;

/**
 * Initialize Google Analytics with consent
 * @param consent User's consent for analytics
 */
export const initializeAnalytics = (consent: { analytics: boolean }): void => {
    if (!isProduction) {
        console.log('[DEV] Analytics initialization skipped in development');
        return;
    }

    if (!consent.analytics) {
        console.log('Analytics disabled due to user consent settings');
        return;
    }

    // This implementation is based on the assumption that Google Analytics
    // is included in the HTML template via script tags.
    // You may need to adjust this based on how GA is actually implemented.
    if (typeof window !== 'undefined' && 'gtag' in window) {
        // Safe cast for TypeScript - gtag is defined on window by Google Analytics script
        const gtag = (window as any).gtag;

        // Initialize Google Analytics with consent
        gtag('consent', 'update', {
            'analytics_storage': 'granted'
        });

        console.log('Google Analytics initialized with consent');
        gaInitialized = true;
    } else {
        console.warn('Google Analytics not found in window object');
    }
};

/**
 * Update analytics consent
 * @param consent User's consent for analytics
 */
export const updateAnalyticsConsent = (consent: { analytics: boolean }): void => {
    if (!isProduction) {
        console.log(`[DEV] Analytics consent ${consent.analytics ? 'granted' : 'denied'}`);
        return;
    }

    if (typeof window !== 'undefined' && 'gtag' in window) {
        const gtag = (window as any).gtag;

        // Update consent state in Google Analytics
        gtag('consent', 'update', {
            'analytics_storage': consent.analytics ? 'granted' : 'denied'
        });

        console.log(`Analytics consent ${consent.analytics ? 'granted' : 'denied'}`);

        // If consent was just granted and GA hasn't been initialized yet, initialize it
        if (consent.analytics && !gaInitialized) {
            initializeAnalytics(consent);
        }
    }
};

/**
 * Track page view
 * @param pageView Page view details
 * @param consent User's consent for analytics
 */
export const trackPageView = (
    pageView: AnalyticsPageView,
    consent: { analytics: boolean }
): void => {
    if (!isProduction || !consent.analytics) {
        if (!isProduction) console.log(`[DEV] Page view: ${pageView.path}`);
        return;
    }

    if (typeof window !== 'undefined' && 'gtag' in window && gaInitialized) {
        const gtag = (window as any).gtag;

        gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || '', {
            page_path: pageView.path,
            page_title: pageView.title
        });
    }
};

/**
 * Track event
 * @param event Event details
 * @param consent User's consent for analytics
 */
export const trackEvent = (
    event: AnalyticsEvent,
    consent: { analytics: boolean }
): void => {
    if (!isProduction || !consent.analytics) {
        if (!isProduction) {
            console.log(`[DEV] Event: ${event.category} - ${event.action} - ${event.label || ''}`);
        }
        return;
    }

    if (typeof window !== 'undefined' && 'gtag' in window && gaInitialized) {
        const gtag = (window as any).gtag;

        gtag('event', event.action, {
            'event_category': event.category,
            'event_label': event.label,
            'value': event.value
        });
    }
};

/**
 * Optional - Add a consent mode wrapper for Google Analytics
 * If you use GA consent mode, this sets up the initial configuration
 */
export const setupGoogleConsentMode = (): void => {
    if (!isProduction) return;

    if (typeof window !== 'undefined') {
        // Create the gtag function if it doesn't exist
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).gtag = function () {
            (window as any).dataLayer.push(arguments);
        };

        // Initial consent configuration - default to denied until consent is granted
        (window as any).gtag('consent', 'default', {
            'analytics_storage': 'denied'
        });
    }
};
